/**
 * 统计数据配置组件
 */

import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, InputNumber, Switch, Space, message, Popconfirm, Row, Col, Tag, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { statisticsConfigService, StatisticsConfig } from '../../../../../../services/statisticsConfigService';
import { platformService } from '../../../../../../services/platformService';
import { useDict } from '../../../../../../hooks/useDict';

const { TextArea } = Input;
const { Option } = Select;

interface StatisticsConfigComponentProps {
  onStatisticsChange?: (statistics: Record<string, any>) => void;
  initialStatistics?: Record<string, any>;
  onStatisticsValuesChange?: (statisticsValues: Record<string, any>) => void;
}

const StatisticsConfigComponent: React.FC<StatisticsConfigComponentProps> = ({
  onStatisticsChange,
  initialStatistics = {},
  onStatisticsValuesChange
}) => {
  const [configs, setConfigs] = useState<StatisticsConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<StatisticsConfig | null>(null);
  const [form] = Form.useForm();
  const [statisticsValues, setStatisticsValues] = useState<Record<string, any>>(initialStatistics);
  
  const { dictData: categoryDict, loading: categoryLoading, error: categoryError } = useDict('statistics_category');
  const { dictData: dataTypeDict, loading: dataTypeLoading, error: dataTypeError } = useDict('statistics_data_type');
  const { dictData: unitDict, loading: unitLoading, error: unitError } = useDict('statistics_unit');
  const { dictData: statisticsItemDict, loading: statisticsItemLoading, error: statisticsItemError } = useDict('statistics_item');

  // 检查用户登录状态
  const isLoggedIn = localStorage.getItem('admin_token');

  // 加载统计数据配置
  const loadConfigs = async () => {
    setLoading(true);
    try {
      const response = await statisticsConfigService.getConfigTree();
      if (response.code === 200) {
        setConfigs(response.data || []);
      } else {
        message.error('加载统计数据配置失败');
      }
    } catch (error) {
      console.error('加载统计数据配置失败:', error);
      message.error('加载统计数据配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载统计数值
  const loadStatisticsValues = async () => {
    try {
      const response = await platformService.getInfo();
      if (response.code === 200 && response.data) {
        // 处理统计数据
        if (response.data.statisticsMap) {
          // 后端返回的是组合键格式，需要转换为displayName格式
          const displayNameStats = convertCombinedKeysToDisplayKeys(response.data.statisticsMap);
          setStatisticsValues(displayNameStats);
        } else if (response.data.statistics) {
          try {
            const parsedStats = JSON.parse(response.data.statistics);
            // 如果后端返回的是组合键格式，需要转换为displayName格式
            const displayNameStats = convertCombinedKeysToDisplayKeys(parsedStats);
            setStatisticsValues(displayNameStats);
          } catch (e) {
            console.error('解析统计数据失败:', e);
            setStatisticsValues({});
          }
        }
      }
    } catch (error) {
      console.error('加载统计数值失败:', error);
    }
  };

  // 将组合键转换为displayName键
  const convertCombinedKeysToDisplayKeys = (combinedStats: Record<string, any>) => {
    const displayNameStats: Record<string, any> = {};
    
    for (const [combinedKey, value] of Object.entries(combinedStats)) {
      // 组合键格式：category_displayName
      const keyParts = combinedKey.split('_');
      if (keyParts.length >= 2) {
        // 取最后一部分作为displayName（处理displayName本身包含下划线的情况）
        const displayName = keyParts.slice(1).join('_');
        
        // 直接使用组合键作为唯一标识，不转换为displayName
        // 这样可以保持不同category下相同displayName的独立性
        displayNameStats[combinedKey] = value;
      } else {
        // 如果不是组合键格式，直接使用
        displayNameStats[combinedKey] = value;
      }
    }
    
    return displayNameStats;
  };

  // 保存统计数据配置
  const handleSave = async (values: any) => {
    try {
      // 检查重复：如果是新增操作，检查是否已存在相同的displayName
      if (!editingConfig?.id) {
        const existingConfig = configs.find(config => 
          config.displayName === values.displayName && 
          config.category === values.category &&
          config.deleted !== 1
        );
        
        if (existingConfig) {
          message.error(`该分类下已存在相同的统计项，请选择其他统计项或分类`);
          return;
        }
      }

      const configData: StatisticsConfig = {
        ...values,
        isActive: values.isActive ? 1 : 0,
      };

      let response;
      if (editingConfig?.id) {
        response = await statisticsConfigService.updateConfig(editingConfig.id, configData);
      } else {
        response = await statisticsConfigService.createConfig(configData);
      }

      if (response.code === 200) {
        message.success(editingConfig?.id ? '更新成功' : '创建成功');
        setModalVisible(false);
        form.resetFields();
        setEditingConfig(null);
        loadConfigs();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      console.error('保存统计数据配置失败:', error);
      message.error('保存失败');
    }
  };

  // 删除统计数据配置
  const handleDelete = async (id: number) => {
    try {
      // 查找要删除的配置，以便清理对应的统计数据值
      const configToDelete = configs.find(config => config.id === id);
      
      const response = await statisticsConfigService.deleteConfig(id);
      if (response.code === 200) {
        message.success('删除成功');
        
        // 如果配置存在，清理对应的统计数据值
        if (configToDelete) {
          const uniqueKey = `${configToDelete.category}_${configToDelete.displayName}`;
          const newStatisticsValues = { ...statisticsValues };
          delete newStatisticsValues[uniqueKey];
          setStatisticsValues(newStatisticsValues);
          
          // 通知父组件更新统计数据值
          onStatisticsValuesChange?.(newStatisticsValues);
        }
        
        loadConfigs();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      console.error('删除统计数据配置失败:', error);
      message.error('删除失败');
    }
  };

  // 处理统计数据值变化
  const handleStatisticsValueChange = (record: StatisticsConfig, value: any) => {
    // 使用配置记录的category和displayName创建唯一键
    const uniqueKey = `${record.category}_${record.displayName}`;
    const newStatistics = { ...statisticsValues, [uniqueKey]: value };
    setStatisticsValues(newStatistics);
    
    // 直接传递组合键格式给父组件
    onStatisticsChange?.(newStatistics);
    onStatisticsValuesChange?.(newStatistics);
  };



  // 打开编辑模态框
  const handleEdit = (config: StatisticsConfig) => {
    setEditingConfig(config);
    form.setFieldsValue({
      ...config,
      isActive: config.isActive === 1,
    });
    setModalVisible(true);
  };

  // 打开新增模态框
  const handleAdd = () => {
    setEditingConfig(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 关闭模态框
  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingConfig(null);
  };

  // 表格列定义 - 优化显示列
  const columns = [
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => {
        const categoryItem = categoryDict.find(item => item.dictValue === category);
        return <Tag color="blue">{categoryItem?.dictLabel || category}</Tag>;
      },
    },
    {
      title: '显示名称',
      dataIndex: 'displayName',
      key: 'displayName',
      width: 400,
      ellipsis: true,
      render: (displayName: string) => {
        const statisticsItem = statisticsItemDict.find(item => item.dictValue === displayName);
        return <span>{statisticsItem?.dictLabel || displayName}</span>;
      },
    },
    {
      title: '单位',
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
      align: 'center' as const,
      render: (unit: string) => {
        const unitItem = unitDict.find(item => item.dictValue === unit);
        return <Tag color="orange">{unitItem?.dictLabel || unit}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      align: 'center' as const,
      render: (_: any, record: StatisticsConfig) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个配置吗？"
            onConfirm={() => handleDelete(record.id!)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 统计数据值输入列
  const statisticsColumns = [
    {
      title: '统计项',
      dataIndex: 'displayName',
      key: 'displayName',
      width: 200,
      render: (displayName: string, record: StatisticsConfig) => {
        // 通过数据字典获取中文显示名称
        const statisticsItem = statisticsItemDict.find(item => item.dictValue === displayName);
        const chineseName = statisticsItem?.dictLabel || displayName;
        
        // 通过数据字典获取分类的中文显示名称
        const categoryItem = categoryDict.find(item => item.dictValue === record.category);
        const chineseCategory = categoryItem?.dictLabel || record.category;
        
        return (
          <div style={{ fontSize: '12px', color: '#333' }}>
            {chineseName}
          </div>
        );
      },
    },
    {
      title: '当前值',
      key: 'value',
      width: 250,
      render: (_: any, record: StatisticsConfig) => {
        // 使用组合键作为唯一标识
        const combinedKey = `${record.category}_${record.displayName}`;
        const currentValue = statisticsValues[combinedKey] || '';
        
        const statisticsItem = statisticsItemDict.find(item => item.dictValue === record.displayName);
        const displayName = statisticsItem?.dictLabel || record.displayName;
        const unitItem = unitDict.find(item => item.dictValue === record.unit);
        const unitDisplay = unitItem?.dictLabel || record.unit;
        
        return (
          <Input
            value={currentValue}
            onChange={(e) => handleStatisticsValueChange(record, e.target.value)}
            placeholder={`请输入${displayName}`}
            suffix={unitDisplay}
          />
        );
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
  ];

  useEffect(() => {
    loadConfigs();
    loadStatisticsValues();
  }, []);



  // 按分类分组显示统计数据
  const groupedConfigs = configs.reduce((acc, config) => {
    if (!acc[config.category]) {
      acc[config.category] = [];
    }
    acc[config.category].push(config);
    return acc;
  }, {} as Record<string, StatisticsConfig[]>);

  // 如果用户未登录，显示提示信息
  if (!isLoggedIn) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ color: '#999', fontSize: '16px' }}>
          请先登录以查看统计数据配置
        </div>
      </div>
    );
  }

  return (
    <div>
      <Card
        title="统计数据配置"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={categoryLoading || dataTypeLoading || unitLoading || statisticsItemLoading}
            >
              新增配置
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadConfigs}
            >
              刷新
            </Button>
          </Space>
        }
      >
        {/* 错误信息显示 */}
        {(categoryError || dataTypeError || unitError || statisticsItemError) && (
          <div style={{ marginBottom: 16, padding: 12, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 6 }}>
            <div style={{ color: '#ff4d4f', marginBottom: 8 }}>
              <strong>数据字典加载失败：</strong>
            </div>
            {categoryError && <div>分类字典: {categoryError}</div>}
            {dataTypeError && <div>数据类型字典: {dataTypeError}</div>}
            {unitError && <div>单位字典: {unitError}</div>}
            {statisticsItemError && <div>统计项字典: {statisticsItemError}</div>}
            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              请检查数据库是否包含统计数据相关的字典数据，或联系管理员。
            </div>
          </div>
        )}
        <Table
          columns={columns}
          dataSource={configs}
          loading={loading}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 750 }}
        />
      </Card>

      <Card
        title="统计数据值设置"
        style={{ marginTop: 16 }}
      >
        {Object.entries(groupedConfigs).map(([category, categoryConfigs]) => {
          const categoryItem = categoryDict.find(item => item.dictValue === category);
          return (
            <div key={category} style={{ marginBottom: 24 }}>
              <Divider orientation="left">
                <Tag color="blue">{categoryItem?.dictLabel || category}</Tag>
              </Divider>
              <Table
                columns={statisticsColumns}
                dataSource={categoryConfigs}
                pagination={false}
                size="small"
                rowKey="id"
                scroll={{ x: 600 }}
              />
            </div>
          );
        })}
      </Card>

      {/* 编辑模态框 */}
      <Modal
        title={editingConfig ? '编辑统计数据配置' : '新增统计数据配置'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={handleCancel}
        width={600}
        destroyOnHidden
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="分类"
                rules={[{ required: true, message: '请选择分类' }]}
              >
                <Select placeholder="请选择分类">
                  {categoryDict.map(item => (
                    <Option key={item.dictValue} value={item.dictValue}>
                      {item.dictLabel}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="displayName"
                label="统计项"
                rules={[{ required: true, message: '请选择统计项' }]}
              >
                <Select placeholder="请选择统计项" allowClear>
                  {statisticsItemDict.map(item => (
                    <Option key={item.dictValue} value={item.dictValue}>
                      {item.dictLabel}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="dataType"
                label="数据类型"
                rules={[{ required: true, message: '请选择数据类型' }]}
              >
                <Select placeholder="请选择数据类型">
                  {dataTypeDict.map(item => (
                    <Option key={item.dictValue} value={item.dictValue}>
                      {item.dictLabel}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="unit"
                label="单位"
              >
                <Select placeholder="请选择单位" allowClear>
                  {unitDict.map(item => (
                    <Option key={item.dictValue} value={item.dictValue}>
                      {item.dictLabel}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="排序号"
                rules={[{ required: true, message: '请输入排序号' }]}
              >
                <InputNumber min={0} placeholder="请输入排序号" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label="是否启用"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StatisticsConfigComponent;